import { useMutation } from '@apollo/client';
import { Formik } from 'formik';
import React, { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import styled from 'styled-components';
import Body from 'components/common/body';
import SimpleButton from 'components/common/button/simple';
import Heading from 'components/common/heading';
import FadeIn from 'components/common/transitions/FadeIn';
import { TextInput } from 'components/form/input';
import { TextArea } from 'components/form/textArea';
import { ADMIN_EMAIL_ADDRESS } from 'constants/env';
import {
  AddContactSubmissionMutation,
  AddContactSubmissionMutationVariables,
  ContactSubmissionType,
} from 'graphql/generated';
import { theme } from 'utils/theme';
import { ADD_CONTACT_SUBMISSION } from './waitlist.query';

const JoinTheWaitlistDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 30px 10px;
`;

const Form = styled.form`
  margin: 20px 0;
  width: 100%;
`;

interface JoinTheWaitlistProps {
  title?: string;
  placeholders?: {
    name?: string;
    email?: string;
    message?: string;
  };
}

const JoinTheWaitlist = ({ title, placeholders }: JoinTheWaitlistProps) => {
  const [successMessage, setSuccessMessage] = useState<null | string>(null);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [successOrErrorInView, setSuccessOrErrorInView] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [addContactSubmission, { loading: addContactSubmissionLoading }] = useMutation<
    AddContactSubmissionMutation,
    AddContactSubmissionMutationVariables
  >(ADD_CONTACT_SUBMISSION);

  return (
    <JoinTheWaitlistDiv>
      <Heading level={300}>{title || 'Join the waitlist now!'}</Heading>
      <Formik
        initialValues={{ name: '', email: '', message: '' }}
        validate={(values) => {
          let errors = {};
          if (!values.name) {
            errors = { ...errors, name: 'Please add your name' };
          }
          if (!values.email) {
            errors = { ...errors, email: 'Please add your email' };
          }
          if (!values.message) {
            errors = { ...errors, message: 'Let us know how you share experiences!' };
          }
          return errors;
        }}
        onSubmit={async ({ name, email, message }, { setSubmitting, resetForm }) => {
          setSubmitting(true);

          const recaptchaToken = await executeRecaptcha?.('JoinTheWaitlist');

          const { data: addContactSubmissionResult } = await addContactSubmission({
            variables: {
              name,
              email,
              message,
              type: ContactSubmissionType.JoinWaitlist,
              gRecaptchaToken: recaptchaToken || '',
            },
          });

          setSubmitting(false);
          resetForm();

          if (addContactSubmissionResult?.addContactSubmission.ok) {
            setSuccessMessage(`Thank you. I'll be in touch soon!`);
          } else {
            setErrorMessage(
              `An error occured. Please try again later or email ${ADMIN_EMAIL_ADDRESS}`,
            );
          }

          setSuccessOrErrorInView(true);
          setTimeout(() => {
            setSuccessMessage(null);
            setErrorMessage(null);
          }, 4000);
        }}
      >
        {({ isSubmitting, handleSubmit, isValid, submitForm }) => (
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <TextInput
              id="name"
              label="Your name"
              name="name"
              type="text"
              placeholder={placeholders?.name || 'Frequent traveler'}
            />
            <TextInput
              id="email"
              label="Your email"
              name="email"
              type="email"
              placeholder={placeholders?.email || 'frequent@traveler.com'}
              description={`Won't be shared with anybody else 🙂`}
            />
            <TextArea
              id="message"
              label="What's the best way you share things you do while traveling?"
              name="message"
              placeholder={placeholders?.message || 'I post on Instagram!'}
            />
            <SimpleButton
              onClick={submitForm}
              disabled={isSubmitting || !isValid || addContactSubmissionLoading}
              backgroundColor={theme.customColors.primary}
            >
              <Body color={theme.colors.black}>
                {addContactSubmissionLoading ? 'Submitting..' : 'Submit'}
              </Body>
            </SimpleButton>
            <FadeIn inView={successOrErrorInView}>
              <>
                {successMessage && <Body>{successMessage}</Body>}
                {errorMessage && <Body color={theme.customColors.error}>{errorMessage}</Body>}
              </>
            </FadeIn>
          </Form>
        )}
      </Formik>
    </JoinTheWaitlistDiv>
  );
};

export default JoinTheWaitlist;
